// Cache sur le poste (IndexedDB, store "cachedata") des données du module Prévisions & Performance.
// Une donnée est servie sans appeler le serveur tant qu'elle a moins de sa durée de vie ET qu'elle a
// été enregistrée le même jour : au changement de date elle est écrasée (les prévisions se calculent
// sur les ventes jusqu'à hier, une donnée de la veille serait décalée d'un jour).
// Durées : 12 h par défaut, 5 min pour le réapprovisionnement (le stock change à chaque vente).
// Le bouton Actualiser passe { force: true } pour ignorer le cache.
// Même principe que service/cacheTableauDeBord.js, avec sa propre durée et la date dans la valeur.
import { getData, saveOrUpdateData, deleteData } from "@/service/indexedDBService";

export const TTL_PP = 12 * 60 * 60 * 1000;
export const TTL_PP_STOCK = 5 * 60 * 1000;

/* Date locale AAAA-MM-JJ (pas toISOString, qui passe en UTC). */
function jourLocal(t) {
    const d = new Date(t);
    const p = (n) => String(n).padStart(2, "0");
    return d.getFullYear() + "-" + p(d.getMonth() + 1) + "-" + p(d.getDate());
}

// state.appsociete est un JWT regénéré à chaque chargement de page (son champ iat change) :
// la clé du cache utilise le champ « token » de sa charge utile, stable pour la session de la société.
function societeStable(societe) {
    try {
        const charge = String(societe).split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const json = JSON.parse(atob(charge + "=".repeat((4 - (charge.length % 4)) % 4)));
        return json.token || societe;
    } catch (e) {
        return societe;
    }
}

function clePP(societe, onglet, params) {
    return `pp_${onglet}_${societeStable(societe)}_${JSON.stringify(params || {})}`;
}

// Renvoie { data, t } si la donnée est encore valable, sinon null (et la supprime).
export async function lireCachePP(societe, onglet, params, ttl = TTL_PP) {
    if (!societe) return null;
    const cle = clePP(societe, onglet, params);
    try {
        const ligne = await getData("cachedata", cle);
        if (!ligne || typeof ligne.t !== "number" || ligne.data === undefined) return null;
        if (Date.now() - ligne.t > ttl || ligne.jour !== jourLocal(Date.now())) {
            await deleteData("cachedata", cle);
            return null;
        }
        return ligne;
    } catch (error) {
        console.error('Error reading prevision cache:', error);
        return null;
    }
}

export async function ecrireCachePP(societe, onglet, params, data) {
    if (!societe || data === undefined || data === null) return;
    try {
        const t = Date.now();
        await saveOrUpdateData("cachedata", clePP(societe, onglet, params), { t, jour: jourLocal(t), data });
    } catch (error) {
        console.error('Error saving prevision cache:', error);
    }
}

// Lit le cache, sinon appelle charger() (qui renvoie la donnée serveur, ou null en cas d'échec) et l'enregistre.
export async function avecCachePP(societe, onglet, params, charger, { force = false, ttl = TTL_PP } = {}) {
    if (!force) {
        const ligne = await lireCachePP(societe, onglet, params, ttl);
        if (ligne) return { data: ligne.data, t: ligne.t, cache: true };
    }
    const data = await charger();
    if (data !== undefined && data !== null) await ecrireCachePP(societe, onglet, params, data);
    return { data, t: Date.now(), cache: false };
}
